import React, { useState, useRef, useEffect, useContext } from "react";
import Modal from "./modal";
import StatusChoiceModal from "./statusChoiceModal";
import DiagnosticModal from "./DiagnosticModal";
import './ReproStyle.css'
import { updateReproduction, updateReproductionE } from "../../../api/reproductionApi";
import './ReproWheel.css'
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import repro from '../../../cow.jpg';
import { useTranslation } from "react-i18next";
import { FiCalendar, FiHash, FiTag, FiEdit2, FiInfo, FiRefreshCw, FiSearch, FiLoader, FiZoomIn, FiZoomOut, FiMaximize, FiAlertCircle, FiClock, FiActivity } from "react-icons/fi";
import { GiCow } from "react-icons/gi";
import { AuthContext } from "../../../Contexts/AuthContext";

const statusColors = {
    "Vêlage": "#ff2911ff",
    'Avortement': '#000000ff',
    "Saillie": "#f1c40f",
    "Oestrus": "#f1c40f",
    "Gestation": "#0746f3f0",
    "Repos": "#27ae60",
};

const ringRadius = 500;
const circumference = 2 * Math.PI * ringRadius;

const segments = [
    {
        label: "Repos",
        color: "#27ae60",
        value: 60.5 / 365
    }, {
        label: "Vêlage",
        color: "#ff2911ff",
        value: 83 / 365
    },
    {
        label: "Saillie",
        color: "#f1c40f",
        value: 89 / 365
    },
    {
        label: "Gestation",
        color: "#0746f3f0",
        value: 144 / 365
    },
];

const phaseStartDay = {
    "Avortement": 0,
    "Vêlage": 0,
    "Saillie": 84,
    "Oestrus": 84,
    "Gestation": 84,
    "Repos": 84
};

const phaseLength = {
    "Avortement": 360,
    "Vêlage": 360,
    "Saillie": 360,
    "Oestrus": 360,
    "Gestation": 360,
    "Repos": 360
};

function daysBetween(from, to) {
    return Math.floor((to - from) / (1000 * 60 * 60 * 24));
}

function getMonthAngle(dateString) {
    const d = new Date(dateString);
    const year = d.getFullYear();
    const startOfYear = new Date(year, 0, 1);

    const dayOfYear = Math.floor((d - startOfYear) / (1000 * 60 * 60 * 24)) + 1;

    return (dayOfYear / 365) * 360;
}

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getRelevantDate(reproduction) {
    const status = reproduction.status?.titre;

    switch (status) {
        case "Vêlage":
            return reproduction.date_velage || reproduction.date_saille || reproduction.date_gestation;
        case "Avortement":
            return reproduction.date_avortement || reproduction.date_velage || reproduction.date_saille || reproduction.date_gestation;
        case "Saillie":
            return reproduction.date_saille || reproduction.date_gestation || reproduction.date_velage;
        case "Oestrus":
            return reproduction.date_saille || reproduction.date_gestation || reproduction.date_velage;
        case "Gestation":
            return reproduction.date_gestation || reproduction.date_saille;
        case "Repos":
            return reproduction.date_gestation || reproduction.date_saille;
        default:
            return reproduction.date_gestation || reproduction.date_saille || reproduction.date_velage;
    }
}

function isEligibleReproduction(reproduction, today) {
    if (!reproduction?.animal || !reproduction?.status) return false;

    const birthDate = new Date(reproduction.animal.date_naissance);
    if (isNaN(birthDate.getTime())) return false;

    const daysOld = (today - birthDate) / (1000 * 60 * 60 * 24);
    if (daysOld < 365) return false;
    if (reproduction.status.titre === 'croissance') return false;

    return Boolean(
        reproduction.date_saille ||
        reproduction.date_gestation ||
        reproduction.date_velage ||
        reproduction.date_avortement
    );
}

function getAttentionState(reproduction, today) {
    const status = reproduction.status?.titre;
    const relevantDateValue = getRelevantDate(reproduction);
    const relevantDate = relevantDateValue ? new Date(relevantDateValue) : null;
    const expectedCalvingDate = reproduction.date_prevu_velage ? new Date(reproduction.date_prevu_velage) : null;
    const daysSinceRelevant = relevantDate && !isNaN(relevantDate.getTime()) ? daysBetween(relevantDate, today) : null;
    const daysToExpected = expectedCalvingDate && !isNaN(expectedCalvingDate.getTime()) ? daysBetween(today, expectedCalvingDate) : null;

    if (status === 'Avortement') {
        return { level: 'critical', score: 100, label: 'Critical follow-up', labelKey: 'criticalFollowUp', meta: 'Immediate intervention', metaKey: 'immediateIntervention' };
    }

    if (status === 'Gestation' && daysToExpected !== null && daysToExpected < 0) {
        return { level: 'critical', score: 90, label: 'Calving overdue', labelKey: 'calvingOverdue', meta: `${Math.abs(daysToExpected)}d overdue`, metaKey: 'daysOverdue', days: Math.abs(daysToExpected) };
    }

    if (status === 'Gestation' && daysToExpected !== null && daysToExpected <= 14) {
        return { level: 'warning', score: 80, label: 'Due soon', labelKey: 'dueSoon', meta: `${daysToExpected}d to calving`, metaKey: 'daysToCalving', days: daysToExpected };
    }

    if ((status === 'Saillie' || status === 'Oestrus') && daysSinceRelevant !== null && daysSinceRelevant >= 21) {
        return { level: 'warning', score: 70, label: 'Check diagnosis', labelKey: 'checkDiagnosis', meta: `${daysSinceRelevant}d since mating`, metaKey: 'daysSinceMating', days: daysSinceRelevant };
    }

    if (status === 'Repos' && daysSinceRelevant !== null && daysSinceRelevant >= 60) {
        return { level: 'watch', score: 50, label: 'Extended rest phase', labelKey: 'extendedRest', meta: `${daysSinceRelevant}d in rest`, metaKey: 'daysInRest', days: daysSinceRelevant };
    }

    return { level: 'stable', score: 0, label: 'On track', labelKey: 'onTrack', meta: status || 'Unknown' };
}

const statusLegendMap = {
    "Vêlage": "velage",
    "Avortement": "avortement",
    "Saillie": "saillie",
    "Oestrus": "oestrus",
    "Gestation": "gestation",
    "Repos": "repos",
};

export default function ReproWheel({ reproductions, onStatusChange, loading }) {
    const { t } = useTranslation();
    const { user } = useContext(AuthContext);
    const isEleveur = user?.role === 'eleveur';

    const getStatusLabel = (status) => {
        const key = statusLegendMap[status];
        return key ? t(`reproWheel.legend.${key}`) : status;
    };

    const getAttentionLabel = (key, fallback) => t(`reproWheel.attentionLabels.${key}`, { defaultValue: fallback });

    const radius = 450;
    const center = 510;
    const today = new Date();
    const todayDateString = today.toISOString().split('T')[0];
    const todayAngle = getMonthAngle(todayDateString);
    const rotationAngle = 181.5 - todayAngle;

    const [selected, setSelected] = useState(null);
    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const [showDiagnostic, setShowDiagnostic] = useState(false);

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [activeFilter, setActiveFilter] = useState('all');

    const [searchQuery, setSearchQuery] = useState('');
    const [searchResult, setSearchResult] = useState(null);
    const monthNames = t("reproWheel.monthNames", {
        returnObjects: true,
        defaultValue: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    });
    const containerRef = useRef(null);
    const svgRef = useRef(null);
    const navigate = useNavigate();

    const eligibleReproductions = reproductions.filter((reproduction) => isEligibleReproduction(reproduction, today));
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filteredReproductions = eligibleReproductions.filter((reproduction) => {
        const attention = getAttentionState(reproduction, today);
        const matchesFilter = activeFilter === 'all'
            ? true
            : activeFilter === 'attention'
                ? attention.level !== 'stable'
                : reproduction.status?.titre === activeFilter;

        const matchesQuery = !normalizedQuery ||
            reproduction.animal?.numero?.toString().toLowerCase().includes(normalizedQuery) ||
            reproduction.animal?.nom?.toLowerCase().includes(normalizedQuery);

        return matchesFilter && matchesQuery;
    });

    const gestationCount = eligibleReproductions.filter((reproduction) => reproduction.status?.titre === 'Gestation').length;
    const attentionQueue = eligibleReproductions
        .map((reproduction) => ({
            reproduction,
            attention: (() => {
                const a = getAttentionState(reproduction, today);
                a.translatedLabel = getAttentionLabel(a.labelKey, a.label);
                a.translatedMeta = a.metaKey ? t(`reproWheel.attentionLabels.${a.metaKey}`, { days: a.days, defaultValue: a.meta }) : a.meta;
                return a;
            })()
        }))
        .filter(({ attention }) => attention.level !== 'stable')
        .sort((left, right) => right.attention.score - left.attention.score)
        .slice(0, 5);

    const dueSoonCount = eligibleReproductions.filter(({ date_prevu_velage, status }) => {
        if (status?.titre !== 'Gestation' || !date_prevu_velage) return false;
        const dueInDays = daysBetween(today, new Date(date_prevu_velage));
        return dueInDays >= 0 && dueInDays <= 14;
    }).length;

    const statusBreakdown = ["Gestation", "Saillie", "Oestrus", "Repos", "Vêlage", "Avortement"]
        .map((status) => ({
            status,
            color: statusColors[status],
            count: eligibleReproductions.filter((reproduction) => reproduction.status?.titre === status).length
        }))
        .filter((item) => item.count > 0);

    const featuredAnimal = selected || searchResult || attentionQueue[0]?.reproduction || filteredReproductions[0] || null;
    const featuredAttention = featuredAnimal ? (() => {
        const a = getAttentionState(featuredAnimal, today);
        a.translatedLabel = getAttentionLabel(a.labelKey, a.label);
        a.translatedMeta = a.metaKey ? t(`reproWheel.attentionLabels.${a.metaKey}`, { days: a.days, defaultValue: a.meta }) : a.meta;
        return a;
    })() : null;

    const filterOptions = [
        { key: 'all', label: t('reproWheel.filters.all', { defaultValue: 'All herd' }) },
        { key: 'attention', label: t('reproWheel.filters.attention', { defaultValue: 'Attention' }) },
        { key: 'Gestation', label: t('reproWheel.legend.gestation') },
        { key: 'Saillie', label: t('reproWheel.legend.saillie') },
        { key: 'Repos', label: t('reproWheel.legend.repos') },
        { key: 'Avortement', label: t('reproWheel.legend.avortement') },
    ];

    useEffect(() => {
        if (!normalizedQuery) {
            setSearchResult(null);
            return;
        }

        const found = eligibleReproductions.find((reproduction) =>
            reproduction.animal?.numero?.toString().toLowerCase().includes(normalizedQuery) ||
            reproduction.animal?.nom?.toLowerCase().includes(normalizedQuery)
        );

        setSearchResult(found || null);
    }, [normalizedQuery, reproductions]);

    const handleMouseDown = (e) => {
        if (e.button === 0) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - pan.x,
                y: e.clientY - pan.y
            });
        }
    };

    const handleWheel = (e) => {
        if (!e.ctrlKey) return;
        e.preventDefault();

        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const delta = e.deltaY * -0.001;
        const newZoom = Math.min(Math.max(0.5, zoom + delta), 2);

        const zoomPoint = {
            x: (mouseX - pan.x) / zoom,
            y: (mouseY - pan.y) / zoom
        };

        const newPan = {
            x: mouseX - zoomPoint.x * newZoom,
            y: mouseY - zoomPoint.y * newZoom
        };

        setZoom(newZoom);
        setPan(newPan);
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            let newX = e.clientX - dragStart.x;
            let newY = e.clientY - dragStart.y;
            setPan({ x: newX, y: newY });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    function openPopup(repro) {
        setSelected(repro);
    }

    function closePopup() {
        setSelected(null);
    }

    function handleChangeStatus(repro) {
        setSelected(repro);
        setShowChoiceModal(true);
    }

    function onSelectNext(next) {
        if (!selected || !selected.id) {
            return;
        }
        setShowChoiceModal(false);

        //  DIAGNOSTIC
        if (next === "Diagnostic") {
            const saillieDate = new Date(selected.date_saille);
            const todayDate = new Date();
            const saillieDay = saillieDate.setHours(0, 0, 0, 0);
            const todayDay = todayDate.setHours(0, 0, 0, 0);

            if (saillieDay === todayDay) {
                swal(t('reproWheel.swal.diagnosticSameDayTitle'), t('reproWheel.swal.diagnosticSameDayText'), 'error');
                return;
            }
            setShowDiagnostic(true);
            return;
        }

        if (next === "Saillie" || next === "Oestrus") {
            if (!selected.date_velage) {
                executeStatusChange(next);
                return;
            }

            const velageDate = new Date(selected.date_velage);
            velageDate.setHours(0, 0, 0, 0);
            const todayDate = new Date();
            todayDate.setHours(0, 0, 0, 0);

            const daysSinceVelage = Math.floor((todayDate - velageDate) / (24 * 60 * 60 * 1000));

            if (daysSinceVelage < 83) {
                swal({
                    title: t("reproWheel.swal.alert83Title"),
                    text: t("reproWheel.swal.alert83Text", { days: daysSinceVelage }),
                    icon: "warning",
                    buttons: [t("reproWheel.swal.confirmContinueNo"), t("reproWheel.swal.confirmContinueYes")],
                    dangerMode: true,
                }).then((willContinue) => {
                    if (willContinue) {
                        executeStatusChange(next);
                    } else {
                        setShowChoiceModal(false);
                        setSelected(null);
                    }
                });
                return;
            }
            executeStatusChange(next);
            return;
        }

        if (next === "Vêlage") {
            if (selected.date_gestation) {
                const gestationDate = new Date(selected.date_gestation);
                gestationDate.setHours(0, 0, 0, 0);
                const todayDate = new Date();
                todayDate.setHours(0, 0, 0, 0);

                if (gestationDate.getTime() === todayDate.getTime()) {
                    swal(t('reproWheel.swal.diagnosticSameDayTitle'), t('reproWheel.swal.diagnosticSameDayText'), 'error');
                    return;
                }
            }

            swal({
                title: t("reproWheel.swal.newAnimalTitle"),
                text: t("reproWheel.swal.newAnimalText"),
                icon: "success",
                buttons: [t("reproWheel.swal.newAnimalNo"), t("reproWheel.swal.newAnimalYes")],
            }).then((willAdd) => {
                if (willAdd) {
                    executeStatusChange(next).then(() => {
                        navigate(`${isEleveur ? '/eleveur' : '/admin'}/add-animal/` + selected.animal.id);
                    });
                } else {
                    swal(t("reproWheel.swal.newAnimalInfo"));
                    executeStatusChange(next);
                }
            });
            return;
        }

        if (next === "Supprimer événement") {
            swal({
                title: t("reproWheel.swal.deleteEventTitle"),
                text: t("reproWheel.swal.deleteEventText"),
                icon: "warning",
                buttons: [t("reproWheel.swal.deleteEventCancel"), t("reproWheel.swal.deleteEventYes")],
                dangerMode: true,
            }).then((willDelete) => {
                if (willDelete) {
                    executeStatusChange("previous");
                } else {
                    setShowChoiceModal(false);
                    setSelected(null);
                }
            });
            return;
        }
        executeStatusChange(next);
    }

    function executeStatusChange(status) {
        if (!selected || !selected.id) {
            return Promise.reject(t('reproWheel.errors.noSelectedItem'));
        }

        return onStatusChange(selected.id, status)
            .then(() => {
                setShowChoiceModal(false);
                setSelected(null);
                swal(t('reproWheel.swal.successStatusUpdated'), '', 'success');
            })
            .catch(err => {
                console.error("Change status failed:", err.response?.data || err.message || err);
                swal(t('reproWheel.swal.errorStatusChange'), '', 'error');
                setShowChoiceModal(false);
            });
    }

    function onDiagnosticResult(result) {
        const id = selected?.id;

        setShowDiagnostic(false);
        setSelected(null);

        if (result === "positif") {
            let referenceDate;
            if (selected.date_saille) {
                referenceDate = new Date(selected.date_saille);
            } else if (selected.date_gestation) {
                referenceDate = new Date(selected.date_gestation);
            } else {
                referenceDate = new Date();
            }

            const prevuVelageDate = new Date(referenceDate.getTime() + (283 * 24 * 60 * 60 * 1000));
            const formattedPrevuVelage = prevuVelageDate.toISOString().split('T')[0];

            let gestationDate;
            if (selected.date_saille) {
                gestationDate = selected.date_saille;
            } else if (selected.date_gestation) {
                gestationDate = selected.date_gestation;
            } else {
                gestationDate = new Date().toISOString().split('T')[0];
            }

            const updateRequest = isEleveur ? updateReproductionE : updateReproduction;

            updateRequest(id, {
                date_gestation: gestationDate,
                date_prevu_velage: formattedPrevuVelage
            })
                .then(() => {
                    return onStatusChange(id, "Gestation");
                })
                .then(() => {
                    swal(t('reproWheel.swal.successDiagnosticPositive'), '', 'success');
                })
                .catch(err => {
                    console.error("Update failed:", err);
                    swal(t('reproWheel.swal.updateFailed'), '', 'error');
                });
        } else {
            onStatusChange(id, "Vêlage", true)
                .then(() => {
                    swal(t('reproWheel.swal.infoDiagnosticNegative'), '', 'info');
                })
                .catch(err => {
                    console.error("Status change failed:", err);
                    swal(t('reproWheel.swal.errorStatusChange'), '', 'error');
                });
        }
    }
    return (
        <>
            <Modal show={!!selected && !showChoiceModal && !showDiagnostic} onClose={closePopup}>
                {selected && (
                    <div className="rp-detail-modal">
                        <h2 className="rp-modal-title"><GiCow /> {t("reproWheel.details.title")}</h2>
                        <div className="rp-detail-grid">
                            <div className="rp-detail-item">
                                <span className="rp-detail-label"><FiHash /> {t("reproWheel.details.number")}</span>
                                <span className="rp-detail-value">{selected.animal.numero}</span>
                            </div>
                            <div className="rp-detail-item">
                                <span className="rp-detail-label"><GiCow /> {t("reproWheel.details.name")}</span>
                                <span className="rp-detail-value">{selected.animal.nom}</span>
                            </div>
                            <div className="rp-detail-item">
                                <span className="rp-detail-label"><FiTag /> {t("reproWheel.details.status")}</span>
                                <span className="rp-detail-value rp-status-pill" style={{ backgroundColor: statusColors[selected.status.titre] || '#e67e22' }}>
                                    {getStatusLabel(selected.status.titre)}
                                </span>
                            </div>
                            <div className="rp-detail-item">
                                <span className="rp-detail-label"><FiCalendar /> {t("reproWheel.details.calvingDate")}</span>
                                <span className="rp-detail-value">{selected.date_velage || t("reproWheel.details.noCalving")}</span>
                            </div>
                            <div className="rp-detail-item">
                                <span className="rp-detail-label"><FiCalendar /> {t("reproWheel.details.matingDate")}</span>
                                <span className="rp-detail-value">{selected.date_saille || t("reproWheel.details.noMating")}</span>
                            </div>
                            <div className="rp-detail-item">
                                <span className="rp-detail-label"><FiCalendar /> {t("reproWheel.details.gestationDate")}</span>
                                <span className="rp-detail-value">{selected.date_gestation || t("reproWheel.details.noGestation")}</span>
                            </div>
                            <div className="rp-detail-item rp-detail-full">
                                <span className="rp-detail-label"><FiCalendar /> {t("reproWheel.details.expectedCalvingDate")}</span>
                                <span className="rp-detail-value">
                                    {(selected.status.titre === 'Repos' || selected.status.titre === 'Gestation')
                                        ? selected.date_prevu_velage || t("reproWheel.details.noExpectedCalving")
                                        : t("reproWheel.details.noExpectedCalving")}
                                </span>
                            </div>
                        </div>
                        <div className="rp-detail-actions">
                            <a href={`${isEleveur ? '/eleveur' : '/admin'}/animal/details/` + selected.animal.id} className="rp-action-btn rp-btn-info">
                                <FiInfo /> {t("reproWheel.details.btnDetails")}
                            </a>
                            <a href={`${isEleveur ? '/eleveur' : '/admin'}/edit/` + selected.animal.id} className="rp-action-btn rp-btn-edit">
                                <FiEdit2 /> {t("reproWheel.details.btnEdit")}
                            </a>
                            <button type="button" onClick={() => { handleChangeStatus(selected); }} className="rp-action-btn rp-btn-status">
                                <FiRefreshCw /> {t("reproWheel.details.btnChangeStatus")}
                            </button>
                        </div>
                    </div>
                )}
            </Modal>

            <StatusChoiceModal
                show={showChoiceModal}
                currentStatus={selected?.status?.titre}
                animalGender={selected?.animal?.genre}
                animalBirthDate={selected?.animal?.date_naissance}
                onSelect={onSelectNext}
                onClose={() => setShowChoiceModal(false)}
            />

            <DiagnosticModal
                show={showDiagnostic}
                onResult={onDiagnosticResult}
                onClose={() => setShowDiagnostic(false)}
            />
            <div className="rp-command-center">
                <div className="rp-command-glow rp-command-glow--one"></div>
                <div className="rp-command-glow rp-command-glow--two"></div>

                <section className="rp-hero-band">
                    <div className="rp-hero-copy">
                        <span className="rp-hero-eyebrow">{t('reproWheel.control.badge', { defaultValue: 'Live reproduction orchestration' })}</span>
                        <h1 className="rp-hero-title">{t('reproWheel.control.title', { defaultValue: 'Premium reproduction command center' })}</h1>
                        <p className="rp-hero-text">{t('reproWheel.control.subtitle', { defaultValue: 'Monitor cycle health, focus urgent cases, and act on the herd from one visual command surface.' })}</p>
                    </div>
                    <div className="rp-hero-signal">
                        <span className="rp-hero-signal-label">{t('reproWheel.control.liveDate', { defaultValue: 'Live cycle date' })}</span>
                        <strong>{todayDateString}</strong>
                        <span>{filteredReproductions.length} / {eligibleReproductions.length} {t('reproWheel.control.visibleAnimals', { defaultValue: 'animals visible' })}</span>
                    </div>
                </section>

                <section className="rp-kpi-grid">
                    <article className="rp-kpi-card">
                        <span className="rp-kpi-icon"><GiCow /></span>
                        <span className="rp-kpi-label">{t('reproWheel.control.managed', { defaultValue: 'Managed reproduction' })}</span>
                        <strong className="rp-kpi-value">{eligibleReproductions.length}</strong>
                        <span className="rp-kpi-meta">{t('reproWheel.control.managedMeta', { defaultValue: 'Active reproductive records in view' })}</span>
                    </article>
                    <article className="rp-kpi-card">
                        <span className="rp-kpi-icon"><FiActivity /></span>
                        <span className="rp-kpi-label">{t('reproWheel.control.gestation', { defaultValue: 'Gestation flow' })}</span>
                        <strong className="rp-kpi-value">{gestationCount}</strong>
                        <span className="rp-kpi-meta">{t('reproWheel.control.gestationMeta', { defaultValue: 'Animals progressing toward calving' })}</span>
                    </article>
                    <article className="rp-kpi-card">
                        <span className="rp-kpi-icon"><FiClock /></span>
                        <span className="rp-kpi-label">{t('reproWheel.control.dueSoon', { defaultValue: 'Due soon' })}</span>
                        <strong className="rp-kpi-value">{dueSoonCount}</strong>
                        <span className="rp-kpi-meta">{t('reproWheel.control.dueSoonMeta', { defaultValue: 'Expected calving within 14 days' })}</span>
                    </article>
                    <article className="rp-kpi-card rp-kpi-card--alert">
                        <span className="rp-kpi-icon"><FiAlertCircle /></span>
                        <span className="rp-kpi-label">{t('reproWheel.control.attention', { defaultValue: 'Attention queue' })}</span>
                        <strong className="rp-kpi-value">{attentionQueue.length}</strong>
                        <span className="rp-kpi-meta">{t('reproWheel.control.attentionMeta', { defaultValue: 'Cases needing review or follow-up' })}</span>
                    </article>
                </section>

                <section className="rp-toolbar-band">
                    <div className="rp-search-shell">
                        <FiSearch className="rp-search-shell-icon" />
                        <input
                            type="text"
                            className="rp-search-shell-input"
                            placeholder={t('reproWheel.searchPlaceholder', { defaultValue: 'Search by number or name…' })}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="rp-chip-row">
                        {filterOptions.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                className={`rp-chip ${activeFilter === filter.key ? 'is-active' : ''}`}
                                onClick={() => setActiveFilter(filter.key)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </section>

                {loading && (
                    <div className="rp-loading rp-loading--panel">
                        <FiLoader className="rp-loading-icon" />
                        <span>{t("reproWheel.loading") || "Loading…"}</span>
                    </div>
                )}

                {!loading && (
                    <div className="rp-workspace-grid">
                        <section className="rp-visual-panel">
                            <div className="rp-panel-head">
                                <div>
                                    <span className="rp-panel-kicker">{t('reproWheel.control.canvasKicker', { defaultValue: 'Cycle map' })}</span>
                                    <h2 className="rp-panel-title">{t('reproWheel.control.canvasTitle', { defaultValue: 'Wheel intelligence view' })}</h2>
                                    <p className="rp-panel-text">{t('reproWheel.control.canvasText', { defaultValue: 'Use filters and search to isolate operational clusters and inspect animals directly on the wheel.' })}</p>
                                </div>
                                <div className="rp-panel-actions">
                                    <div className="rp-zoom-controls">
                                        <button className="rp-zoom-btn" onClick={() => setZoom(z => Math.min(z + 0.15, 2))} title="Zoom in"><FiZoomIn /></button>
                                        <button className="rp-zoom-btn" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Reset"><FiMaximize /></button>
                                        <button className="rp-zoom-btn" onClick={() => setZoom(z => Math.max(z - 0.15, 0.5))} title="Zoom out"><FiZoomOut /></button>
                                    </div>
                                </div>
                            </div>

                            {filteredReproductions.length === 0 && (
                                <div className="rp-empty-state">
                                    <strong>{t('reproWheel.control.emptyTitle', { defaultValue: 'No animals match the current view' })}</strong>
                                    <span>{t('reproWheel.control.emptyText', { defaultValue: 'Reset the filters or search to bring the wheel population back into focus.' })}</span>
                                </div>
                            )}

                            <div
                                className="rp-wheel-container"
                                ref={containerRef}
                                style={{ cursor: isDragging ? "grabbing" : "grab" }}
                                onWheel={handleWheel}
                                onMouseMove={handleMouseMove}
                                onMouseDown={handleMouseDown}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                            >
                                <div className="rp-wheel-statusbar">
                                    <span className="rp-wheel-status-pill">{t('reproWheel.control.focused', { defaultValue: 'Focused view' })}: {activeFilter === 'all' ? t('reproWheel.filters.all', { defaultValue: 'All herd' }) : filterOptions.find((filter) => filter.key === activeFilter)?.label}</span>
                                    <span className="rp-wheel-status-pill">{t('reproWheel.control.matching', { defaultValue: 'Matching animals' })}: {filteredReproductions.length}</span>
                                </div>

                                <div className="rp-wheel-inner">
                                    {featuredAnimal && (
                                        <div className={`rp-center-card rp-center-card--${featuredAttention?.level || 'stable'}`}>
                                            <span className="rp-center-card-label">{t('reproWheel.control.centerTitle', { defaultValue: 'Current focus' })}</span>
                                            <strong>{featuredAnimal.animal?.numero}</strong>
                                            <span>{getStatusLabel(featuredAnimal.status?.titre)}</span>
                                            <small>{featuredAttention?.translatedLabel}</small>
                                        </div>
                                    )}

                                    <svg
                                        ref={svgRef}
                                        viewBox="-200 0 1513 1300"
                                        preserveAspectRatio="xMidYMid meet"
                                        className="rp-wheel-svg"
                                        style={{
                                            transform: `translate(${pan.x}px, 0px) scale(${zoom})`,
                                            transformOrigin: "center center",
                                            transition: isDragging ? "none" : "transform 0.1s ease-out"
                                        }}
                                    >


                                    {(() => {
                                        let dashOffset = 0;
                                        const todayStartAngle = 30.3;
                                        const todayStartAngleRec = 30;

                                        return (
                                            <g>
                                                <g transform={`rotate(${todayStartAngle}, ${center}, ${center})`}>{segments.map((seg, i) => {
                                                    const dash = seg.value * circumference;
                                                    const element = (
                                                        <circle
                                                            key={i}
                                                            cx={center}
                                                            cy={center}
                                                            r={ringRadius}
                                                            fill="none"
                                                            stroke={seg.color}
                                                            strokeWidth="22"
                                                            strokeLinecap="butt"
                                                            strokeDasharray={`${dash} ${circumference}`}
                                                            strokeDashoffset={-dashOffset}
                                                        />
                                                    );
                                                    dashOffset += dash;
                                                    return element;
                                                })}</g>
                                                <g transform={`rotate(${todayStartAngleRec}, ${center}, ${center})`}>

                                                    {(() => {
                                                        const labeledIndices = [0, 6, 12, 18, 21, 24, 29, 33, 37];
                                                        const labeled = t('reproWheel.cycleLabels', { returnObjects: true });
                                                        return Array.from({ length: 72 }, (_, i) => {
                                                            const angle = i * 5;
                                                            let x = center + ringRadius * Math.cos(angle * Math.PI / 180);
                                                            let y = center + ringRadius * Math.sin(angle * Math.PI / 180);
                                                            const elements = [
                                                                <rect
                                                                    key={i}
                                                                    x={x - 10}
                                                                    y={y - 2.5}
                                                                    width="20"
                                                                    height="3"
                                                                    fill="black"
                                                                    transform={`rotate(${angle}, ${x}, ${y})`}
                                                                />
                                                            ];
                                                            if (labeledIndices.includes(i)) {
                                                                const textRadius = ringRadius + 90;
                                                                let LineRadius = ringRadius + 60;
                                                                let textX = center + textRadius * Math.cos(angle * Math.PI / 180);
                                                                let textY = center + textRadius * Math.sin(angle * Math.PI / 180);
                                                                let lineX = center + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                let lineY = center + LineRadius * Math.sin(angle * Math.PI / 180);

                                                                const labelIndex = labeledIndices.indexOf(i);
                                                                if (labelIndex === 6) {
                                                                    y = center + 30 + ringRadius * Math.sin(angle * Math.PI / 180);
                                                                    x = center + 10 + ringRadius * Math.cos(angle * Math.PI / 180);
                                                                    lineX = center + 20 + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                if (labelIndex === 8) {
                                                                    lineX = center + 20 + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                    textX = center - 15 + textRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                if (labelIndex === 0) {
                                                                    lineX = center - 20 + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                if (labelIndex === 7) {
                                                                    lineX = center + 20 + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                    lineY = center - 10 + LineRadius * Math.sin(angle * Math.PI / 180);
                                                                    textX = center - 20 + textRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                if (labelIndex === 2) {
                                                                    LineRadius = ringRadius + 30;
                                                                    lineX = center + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                    lineY = center + LineRadius * Math.sin(angle * Math.PI / 180);
                                                                    y = center + ringRadius * Math.sin(angle * Math.PI / 180);
                                                                    x = center + ringRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                elements.push(<>
                                                                    {labelIndex === 2 && <line x1={lineX + 12} y1={lineY - 6.5} x2={x + 1} y2={lineY + 9} stroke="#640303ff" strokeWidth="2" />}
                                                                    <line x1={lineX} y1={lineY} x2={x} y2={y} stroke={labelIndex === 2 ? "#640303ff" : '#000000ff'} strokeWidth={labelIndex === 2 ? "#fc0404ff" : '#000000ff'} />
                                                                    <text
                                                                        key={`text-${i}`}
                                                                        x={textX}
                                                                        y={textY}
                                                                        fontSize="15"
                                                                        textAnchor="middle"
                                                                        alignmentBaseline="middle"
                                                                        fill="black"
                                                                        transform={`rotate(${-todayStartAngleRec}, ${textX}, ${textY})`}
                                                                    >
                                                                        {labeled[labelIndex].split('\n').map((line, i) => (
                                                                            <tspan x={textX} dy={i === 0 ? 0 : 12}>{line}</tspan>
                                                                        ))}
                                                                    </text></>
                                                                );
                                                            }
                                                            return elements;
                                                        });
                                                    })()}
                                                </g>
                                            </g>
                                        );
                                    })()}

                                    <circle
                                        cx={center}
                                        cy={center}
                                        r={radius}
                                        fill="white"
                                        stroke="#333"
                                        strokeWidth="3"
                                    />

                                    {(() => {
                                        const angle = 90;
                                        const x = center + (radius - 40) * Math.cos(angle * Math.PI / 180);
                                        const y = center + (radius - 40) * Math.sin(angle * Math.PI / 180);

                                        return (
                                            <g>
                                                <line x1={x} y1={y + 40} x2={center} y2={center} stroke="#ff0000ff" />
                                                <line x1={x} y1={y + 40} x2={center} y2={center} stroke="#ff0000ff" />
                                                <text x={x} y={y + 140} fontSize="20" textAnchor="middle" fill="red">
                                                    {t("reproWheel.todayLabel")}
                                                </text>
                                            </g>
                                        );
                                    })()}

                                    <g transform={`rotate(${rotationAngle}, ${center}, ${center})`}>
                                        {monthNames.map((m, i) => {
                                            const angle = i * 30 - 90;
                                            const x = center + radius * Math.cos(angle * Math.PI / 180);
                                            const y = center + radius * Math.sin(angle * Math.PI / 180);
                                            const textX = center + (radius + 20) * Math.cos(angle * Math.PI / 180);
                                            const textY = center + (radius + 20) * Math.sin(angle * Math.PI / 180);

                                            return (
                                                <g key={i}>
                                                    <line x1={center} y1={center} x2={x} y2={y} stroke="#bbb" strokeWidth="1" />
                                                    <text
                                                        x={textX}
                                                        y={textY}
                                                        fontSize="12"
                                                        textAnchor="middle"
                                                        alignmentBaseline="middle"
                                                        transform={`rotate(${-rotationAngle}, ${textX}, ${textY})`}
                                                    >
                                                        {m}
                                                    </text>
                                                </g>
                                            );
                                        })}

                                        {monthNames.map((m, i) => {
                                            const year = today.getFullYear();
                                            const daysInMonth = getDaysInMonth(year, i);
                                            const monthStartAngle = i * 30 - 90;
                                            const dayAngleStep = 30 / daysInMonth;

                                            return Array.from({ length: Math.floor(daysInMonth / 5) }, (_, j) => {
                                                const day = (j + 1) * 5;
                                                const angle = monthStartAngle + ((day - 1) * dayAngleStep);
                                                const x1 = center + (radius - 60) * Math.cos(angle * Math.PI / 180);
                                                const y1 = center + (radius - 60) * Math.sin(angle * Math.PI / 180);
                                                const x2 = center + (radius - 80) * Math.cos(angle * Math.PI / 180);
                                                const y2 = center + (radius - 80) * Math.sin(angle * Math.PI / 180);
                                                const textX = center + (radius - 90) * Math.cos(angle * Math.PI / 180);
                                                const textY = center + (radius - 90) * Math.sin(angle * Math.PI / 180);

                                                return (
                                                    <g key={`${i}-${day}`}>
                                                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ccc" strokeWidth="1" />
                                                        <text
                                                            x={textX}
                                                            y={textY}
                                                            fontSize="8"
                                                            textAnchor="middle"
                                                            alignmentBaseline="middle"
                                                            fill="#666"
                                                            transform={`rotate(${-rotationAngle}, ${textX}, ${textY})`}
                                                        >
                                                            {day}
                                                        </text>
                                                    </g>
                                                );
                                            });
                                        })}

                                        {(() => {
                                            const animalsByAngle = new Map();

                                            filteredReproductions.forEach((r, index) => {
                                                if (!r?.animal || !r?.status) return;

                                                const dateN = new Date(r.animal.date_naissance);
                                                const dateNDiff = (today - dateN) / (1000 * 60 * 60 * 24);

                                                if (dateNDiff < 365 || (!r.date_saille && !r.date_gestation && !r.date_velage && !r.date_avortement) || r.status.titre === 'croissance') return;

                                                const relevantDate = getRelevantDate(r);
                                                if (!relevantDate) return;

                                                const startDate = new Date(relevantDate);
                                                const diffDays = Math.max(0, daysBetween(startDate, today));

                                                const status = r.status.titre;
                                                const phaseStart = phaseStartDay[status] || 0;
                                                const currentPhaseLength = phaseLength[status] || 0;

                                                const insideSegment = Math.min(diffDays, currentPhaseLength);
                                                const cycleDay = phaseStart + insideSegment;
                                                const angle = (cycleDay / 365) * 360 + todayAngle - 91;
                                                const GROUPING_THRESHOLD = 15;
                                                const angleKey = Math.round(angle / GROUPING_THRESHOLD) * GROUPING_THRESHOLD;

                                                if (!animalsByAngle.has(angleKey)) {
                                                    animalsByAngle.set(angleKey, []);
                                                }

                                                animalsByAngle.get(angleKey).push({
                                                    reproduction: r,
                                                    angle: angle,
                                                    status: status,
                                                    index: index
                                                });
                                            });

                                            const elements = [];

                                            animalsByAngle.forEach((animals, angleKey) => {
                                                const count = animals.length;

                                                animals.forEach((animal, posIndex) => {
                                                    const { reproduction: r, angle, status, index } = animal;

                                                    
                                                    const isSearchMatch = searchResult && searchResult.id === r.id;

                                                    let radiusOffset = 0;
                                                    if (count > 1) {
                                                        const spacing = 35;
                                                        radiusOffset = ((posIndex - (count) / 2) * spacing);
                                                    }

                                                    const adjustedRadius = radius - 40 + radiusOffset;
                                                    const x = center + adjustedRadius * Math.cos(angle * Math.PI / 180);
                                                    const y = center + adjustedRadius * Math.sin(angle * Math.PI / 180);
                                                    const color = statusColors[status] || "#e67e22";

                                                    elements.push(
                                                        <g key={index} onClick={() => openPopup(r)} style={{ cursor: "pointer" }}>
                                                            
                                                            {isSearchMatch && (
                                                                <>
                                                                    <circle 
                                                                        cx={x} 
                                                                        cy={y} 
                                                                        r="25" 
                                                                        fill="none" 
                                                                        stroke="#FFD700" 
                                                                        strokeWidth="3"
                                                                        opacity="0.8"
                                                                    >
                                                                        <animate
                                                                            attributeName="r"
                                                                            from="25"
                                                                            to="35"
                                                                            dur="1s"
                                                                            repeatCount="indefinite"
                                                                        />
                                                                        <animate
                                                                            attributeName="opacity"
                                                                            from="0.8"
                                                                            to="0"
                                                                            dur="1s"
                                                                            repeatCount="indefinite"
                                                                        />
                                                                    </circle>
                                                                    <circle 
                                                                        cx={x} 
                                                                        cy={y} 
                                                                        r="25" 
                                                                        fill="none" 
                                                                        stroke="#FFD700" 
                                                                        strokeWidth="4"
                                                                    />
                                                                </>
                                                            )}
                                                            <circle 
                                                                cx={x} 
                                                                cy={y} 
                                                                r={isSearchMatch ? "18" : "15"} 
                                                                fill={color} 
                                                                stroke={isSearchMatch ? "#FFD700" : "#000"} 
                                                                strokeWidth={isSearchMatch ? "4" : "2"}
                                                            />
                                                            <text
                                                                x={x}
                                                                y={y + 5}
                                                                fontSize={isSearchMatch ? "14" : "12"}
                                                                fontWeight={isSearchMatch ? "bold" : "normal"}
                                                                textAnchor="middle"
                                                                fill="white"
                                                                transform={`rotate(${-rotationAngle}, ${x}, ${y})`}
                                                            >
                                                                {r.animal?.numero}
                                                            </text>
                                                        </g>
                                                    );
                                                });
                                            });

                                            return elements;
                                        })()}
                                    </g>
                                    <defs>
                                        <clipPath id="circleClip">
                                            <circle cx={center} cy={center} r="50" />
                                        </clipPath>
                                    </defs>

                                    <image
                                        href={repro}
                                        x={center - 80}
                                        y={center - 50}
                                        width="160"
                                        clipPath="url(#circleClip)"
                                        preserveAspectRatio="xMidYMid slice"
                                    />
                                    </svg>
                                </div>
                            </div>
                        </section>

                        <aside className="rp-insight-rail">
                            <section className={`rp-rail-card rp-rail-card--feature rp-rail-card--${featuredAttention?.level || 'stable'}`}>
                                <span className="rp-rail-kicker">{t('reproWheel.control.featured', { defaultValue: 'Featured animal' })}</span>
                                {featuredAnimal ? (
                                    <>
                                        <div className="rp-featured-head">
                                            <strong>{featuredAnimal.animal?.numero}</strong>
                                            <span>{featuredAnimal.animal?.nom || t('reproWheel.control.unnamed', { defaultValue: 'Unnamed animal' })}</span>
                                        </div>
                                        <div className="rp-featured-meta">
                                            <span>{getStatusLabel(featuredAnimal.status?.titre)}</span>
                                            <span>{featuredAttention?.translatedLabel}</span>
                                        </div>
                                        <div className="rp-featured-grid">
                                            <div>
                                                <label>{t('reproWheel.details.expectedCalvingDate')}</label>
                                                <strong>{featuredAnimal.date_prevu_velage || t('reproWheel.details.noExpectedCalving')}</strong>
                                            </div>
                                            <div>
                                                <label>{t('reproWheel.details.matingDate')}</label>
                                                <strong>{featuredAnimal.date_saille || t('reproWheel.details.noMating')}</strong>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <span>{t('reproWheel.control.noFeature', { defaultValue: 'Select an animal on the wheel to inspect its operational context.' })}</span>
                                )}
                            </section>

                            <section className="rp-rail-card">
                                <div className="rp-rail-header">
                                    <h3>{t('reproWheel.control.statusMap', { defaultValue: 'Status map' })}</h3>
                                    <span>{eligibleReproductions.length}</span>
                                </div>
                                <div className="rp-status-list">
                                    {statusBreakdown.map((item) => (
                                        <div key={item.status} className="rp-status-row">
                                            <div className="rp-status-main">
                                                <span className="rp-status-dot" style={{ backgroundColor: item.color }}></span>
                                                <span>{getStatusLabel(item.status)}</span>
                                            </div>
                                            <div className="rp-status-bar-track">
                                                <div className="rp-status-bar-fill" style={{ width: `${(item.count / Math.max(eligibleReproductions.length, 1)) * 100}%`, backgroundColor: item.color }}></div>
                                            </div>
                                            <strong>{item.count}</strong>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <section className="rp-rail-card">
                                <div className="rp-rail-header">
                                    <h3>{t('reproWheel.control.queueTitle', { defaultValue: 'Attention queue' })}</h3>
                                    <span>{attentionQueue.length}</span>
                                </div>
                                <div className="rp-queue-list">
                                    {attentionQueue.length === 0 && (
                                        <div className="rp-queue-empty">{t('reproWheel.control.queueEmpty', { defaultValue: 'No urgent cases right now. The cycle map is stable.' })}</div>
                                    )}
                                    {attentionQueue.map(({ reproduction, attention }) => (
                                        <button
                                            key={reproduction.id}
                                            type="button"
                                            className={`rp-queue-item rp-queue-item--${attention.level}`}
                                            onClick={() => openPopup(reproduction)}
                                        >
                                            <div>
                                                <strong>{reproduction.animal?.numero}</strong>
                                                <span>{getStatusLabel(reproduction.status?.titre)}</span>
                                            </div>
                                            <div>
                                                <strong>{attention.translatedLabel}</strong>
                                                <span>{attention.translatedMeta}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </section>
                        </aside>
                    </div>
                )}
            </div>
        </>
    );
}
